import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { theme as antdTheme } from "antd";

const ThemeContext = createContext(null);

const storageKey = "flowwise-theme-mode";

const themeModes = ["light", "black"];

function readStoredMode() {
  if (typeof window === "undefined") return "black";
  const stored = window.localStorage.getItem(storageKey);
  if (themeModes.includes(stored)) return stored;
  return "black";
}

function buildThemeConfig(mode) {
  const isBlack = mode === "black";
  const algorithm = isBlack ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

  const tokens = isBlack
    ? {
        colorPrimary: "#14b8a6",
        colorBgBase: "#121212",
        colorBgLayout: "#121212",
        colorBgContainer: "#181818",
        colorBgElevated: "#202020",
        colorBorder: "#343434",
        colorText: "#f1f1f1",
        colorTextSecondary: "#b4b4b4",
        borderRadius: 14,
      }
    : {
        colorPrimary: "#14b8a6",
        colorBgLayout: "#f3f4f6",
        colorBgContainer: "#ffffff",
        colorBorder: "#d6dde6",
        borderRadius: 14,
      };

  return {
    algorithm,
    token: tokens,
  };
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(readStoredMode);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, mode);
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const themeConfig = useMemo(() => buildThemeConfig(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      toggleMode: () => setMode((prev) => (prev === "light" ? "black" : "light")),
      themeConfig,
      isDark: mode === "black",
    }),
    [mode, themeConfig]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeMode() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeProvider");
  }
  return context;
}

export default ThemeContext;
